function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import Head from "next/head";
import { withGenericConfig } from "/contexts/generic-config-context";
import { withAbTests } from "contexts/ab-tests-context";
import { addGTMVariablesToWindow, updateDataLayerVariables } from "client/services/gtm-service/gtm-service";
import { getJsonLdScript } from "components/core/head/google-structured-data/google-structured-data";
import { getHomepageLocalizedRoutes, LOCALIZED_PRICING_ROUTES } from "constants/localization";
import { ROOT_LINK } from "constants/links";
import FirstFoldImages from "components/core/head/first-fold-images";
import { getMetaTags } from "./meta-tags";
import { getAppIconsLinks } from "./app-icons";
import { getFacebookMetaTags } from "./social-meta-tags/facebook-meta";
import { getTwitterMetaTags } from "./social-meta-tags/twitter-meta";
import FontsComponent from "./fonts-component";
import GoogleTagManagerComponent from "/components/core/google-tag-manager/google-tag-manager-component";
import RedirectToPlatformScript from "./redirect-to-platform-script/redirect-to-platform-script";
import { normalizeUrl, normalizeUrlForCanonical, truncateQueryParams } from "/utils/url";
import SignupPrefetchedAssetsLinksComponent from "./signup-prefetched-assets-links-component";
import buildSeoFAQ from "./faq-seo/faq-seo";
import LiveBoardPrefetchedAssetsLinksComponent from "./liveboard-prefetched-assets-links-component";
import PreconnectLinksComponent from "./preconnect-links-component";
import { isInIframe } from "utils/iframe-utils";
const DEFAULT_TITLE_KEY = "website.meta.title.default";
const DEFAULT_DESCRIPTION_KEY = "website.meta.description.default";
const isProduction = process.env.NODE_ENV === "production";
const isProductionDomain = process.env.MONDAY_BASE_PATH.includes("monday.com");
const HPHead = (props)=>{
    const { clusterId, hasFreeTier, cloudfrontViewerCountry, disableGTM, description, template, localeId, noIndex, noFollow, gtmContainerId, isMobileOrTabletBrowser, pagePath, facebookMetaOverride, twitterMetaOverride, translate, localizedPages, abTests, translateWithParam, title, canonicalUrl, shouldGetSignupPrefetchedAssets, seoFAQ, persistantCookies, preloadFirstFoldImages, addLiveBoardExperience, jsonLdSchema } = props;
    const [isEmbeddedInIframe, setIsEmbeddedInIframe] = useState(false);
    useEffect(()=>{
        setIsEmbeddedInIframe(isInIframe());
    }, []);
    const shouldAddGTM = isProduction && !disableGTM && !isEmbeddedInIframe;
    useEffect(()=>{
        if (shouldAddGTM) {
            addGTMVariablesToWindow({
                localeId,
                clusterId,
                hasFreeTier
            });
            updateDataLayerVariables({
                country: cloudfrontViewerCountry
            });
        }
    }, [
        shouldAddGTM
    ]);
    const getCanonicalUrl = ()=>{
        return normalizeUrlForCanonical(canonicalUrl || pagePath);
    };
    const getTitle = ()=>{
        if (typeof title === "object" && title.key && title.param) {
            return translateWithParam(title.key, title.param);
        }
        return translate(title);
    };
    const isHomepageOrPricingPage = ()=>{
        try {
            if (!pagePath) return false;
            const relevantNormalizedRoutes = [
                ROOT_LINK,
                ...getHomepageLocalizedRoutes(),
                ...LOCALIZED_PRICING_ROUTES
            ];
            const normalizedPath = normalizeUrl(truncateQueryParams(pagePath));
            return relevantNormalizedRoutes.includes(normalizedPath);
        } catch (e) {
            // TODO: talk with Barak - sentry is a pretty big import
            return false;
        }
    };
    const translatedTitle = getTitle();
    const translatedDescription = translate(description);
    // The <Head> component can only receive functions, not other components
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsxs(Head, {
            children: [
                !isProductionDomain && /*#__PURE__*/ _jsx("meta", {
                    name: "robots",
                    content: "noindex, nofollow"
                }),
                !isMobileOrTabletBrowser && isHomepageOrPricingPage() && RedirectToPlatformScript({
                    abTests,
                    persistantCookies
                }),
                /*#__PURE__*/ _jsx("title", {
                    children: translatedTitle
                }),
                /*#__PURE__*/ _jsx("meta", {
                    name: "description",
                    content: translatedDescription
                }),
                /*#__PURE__*/ _jsx("link", {
                    rel: "canonical",
                    href: getCanonicalUrl()
                }),
                FontsComponent(),
                getMetaTags({
                    template,
                    localeId,
                    noIndex,
                    noFollow
                }),
                getAppIconsLinks(),
                getFacebookMetaTags(_object_spread({
                    title: translatedTitle,
                    description: translatedDescription,
                    pagePath
                }, facebookMetaOverride)),
                buildSeoFAQ({
                    seoFAQ,
                    translate
                }),
                getTwitterMetaTags(_object_spread({
                    title: translatedTitle,
                    description: translatedDescription
                }, twitterMetaOverride)),
                getJsonLdScript(jsonLdSchema),
                shouldAddGTM && GoogleTagManagerComponent({
                    gtmId: gtmContainerId
                }),
                localizedPages && localizedPages.map((tp, index)=>/*#__PURE__*/ _jsx("link", {
                        rel: "alternate",
                        hrefLang: tp.locale,
                        href: tp.path
                    }, index)),
                shouldGetSignupPrefetchedAssets && SignupPrefetchedAssetsLinksComponent(),
                addLiveBoardExperience && LiveBoardPrefetchedAssetsLinksComponent(),
                (preloadFirstFoldImages === null || preloadFirstFoldImages === void 0 ? void 0 : preloadFirstFoldImages.length) && FirstFoldImages(preloadFirstFoldImages),
                PreconnectLinksComponent()
            ]
        })
    });
};
HPHead.defaultProps = {
    title: DEFAULT_TITLE_KEY,
    description: DEFAULT_DESCRIPTION_KEY,
    canonicalUrl: "",
    withIntercom: false,
    fontLazyLoad: false,
    disableGTM: false,
    facebookMetaOverride: {},
    twitterMetaOverride: {},
    localizedPages: []
};
export default withAbTests(withGenericConfig(HPHead));
